import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { useInfiniteQuery } from '@tanstack/react-query';
import { createColumnHelper, flexRender, getCoreRowModel, SortingState, useReactTable } from '@tanstack/react-table';

import { AccessModes, Dimensions, SortOrder, Sources } from 'components/insights/constants';
import CircularProgress from 'components/shared/CircularProgress';
import {
  buildMobileGridTemplateColumns,
  DataTableTheme,
  Table as StyledTable,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderFooterRow,
  TableRow,
} from 'components/shared/DataTable';
import ExpandableArea from 'components/shared/ExpandableArea';
import { buildGridTemplateTracks } from 'components/shared/table/buildGridTemplateColumns';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { simpleDate } from 'lib/date';
import { Analytics, retrieveAnalytics } from 'services/api/insights/analytics';
import { serializeSort } from 'stores/helpers';
import {
  getInboundReferralAnalyticsFilters,
  getInboundSlideoutEntityFilter,
  useInboundReferralsInsightsStore,
} from 'stores/inboundReferralsInsightsStore';
import ArrowUpDownIcon from 'svg/ArrowUpDownIcon';

import { slugify } from '../constants';
import InboundReferralApiExport from '../InboundReferralApiExport';

type PatientRow = {
  patientName: string;
  url: string;
  receivingFacilities: string;
  referralSource: string;
  payerSource: string;
  receivedAt: string;
};

const columnHelper = createColumnHelper<PatientRow>();

const columns = [
  columnHelper.accessor('patientName', {
    id: Dimensions.PATIENT_NAME,
    header: 'Patient Name',
    cell: (info) => {
      const name = info.getValue();
      const { url } = info.row.original;
      if (!name) return '—';
      if (!url) return name;
      return (
        <ReferralLink href={url} target='_blank' rel='noopener noreferrer'>
          {name}
        </ReferralLink>
      );
    },
    enableSorting: true,
  }),
  // Every facility the referral was sent to, comma-separated — matches the operational Inbound
  // Referrals list. Not sortable (it's an aggregated list, like that page's SNF column).
  columnHelper.accessor('receivingFacilities', {
    id: Dimensions.RECEIVING_FACILITIES,
    header: 'Receiving SNFs',
    cell: (info) => info.getValue() || '—',
    enableSorting: false,
  }),
  columnHelper.accessor('referralSource', {
    id: Dimensions.REFERRAL_SOURCE,
    header: 'Referral Source',
    cell: (info) => info.getValue() || '—',
    enableSorting: true,
  }),
  columnHelper.accessor('payerSource', {
    id: Dimensions.PAYER_SOURCE,
    header: 'Payer Source',
    cell: (info) => info.getValue() || '—',
    enableSorting: true,
  }),
  columnHelper.accessor('receivedAt', {
    id: Dimensions.DATE,
    header: 'Referral Received Date',
    cell: (info) => (info.getValue() ? simpleDate(info.getValue()) : '—'),
    size: 190,
    enableSorting: true,
  }),
];

// referral_url is exported as its own column (separate from the linked patient name) but isn't
// rendered as a visible column — the patient name is the clickable link in the table.
const DIMENSIONS = [
  Dimensions.PATIENT_NAME,
  Dimensions.REFERRAL_URL,
  Dimensions.RECEIVING_FACILITIES,
  Dimensions.REFERRAL_SOURCE,
  Dimensions.PAYER_SOURCE,
  Dimensions.DATE,
];

const BASE_PARAMS = {
  source: Sources.REFERRAL_INTAKES,
  accessModes: [AccessModes.MANAGER],
  dimensions: DIMENSIONS,
  metrics: [],
};

export default function InboundPatientListSection() {
  const slideoutContext = useInboundReferralsInsightsStore((state) => state.slideoutContext);

  const exportParams = useMemo(() => ({ ...BASE_PARAMS, sortBy: `${Dimensions.DATE} ${SortOrder.DESC}` }), []);

  const exportExtraFilters = useMemo(
    () => (slideoutContext ? getInboundSlideoutEntityFilter(slideoutContext) : null),
    [slideoutContext]
  );

  const exportFilename = useMemo(() => {
    if (!slideoutContext) return undefined;
    const suffix = slideoutContext.kind === 'metric' ? slideoutContext.metric : slugify(slideoutContext.entityName);
    return `inbound-referrals${suffix ? `-${suffix}` : ''}`;
  }, [slideoutContext]);

  // ExpandableArea's handle bar spans the full parent width — keep Wrapper unpadded.
  return (
    <Wrapper>
      <Header>
        <Title>Referrals</Title>
        <InboundReferralApiExport params={exportParams} extraFilters={exportExtraFilters} filename={exportFilename} />
      </Header>
      <ExpandableArea mobileFlush>
        <PatientListTable />
      </ExpandableArea>
    </Wrapper>
  );
}

// Split so the query only fires once expanded — ExpandableArea unmounts children when collapsed.
function PatientListTable() {
  const baseFilters = useInboundReferralsInsightsStore((state) => getInboundReferralAnalyticsFilters(state));
  const slideoutContext = useInboundReferralsInsightsStore((state) => state.slideoutContext);

  const analyticsFilters = useMemo(() => {
    if (!baseFilters) return null;
    const entityFilter = slideoutContext ? getInboundSlideoutEntityFilter(slideoutContext) : null;
    return { ...baseFilters, ...(entityFilter ?? {}) };
  }, [baseFilters, slideoutContext]);

  const [sort, setSort] = useState<SortingState>([{ id: Dimensions.DATE, desc: true }]);

  const queryParams = useMemo(() => ({ ...BASE_PARAMS, sortBy: serializeSort(sort), pageSize: 10 }), [sort]);

  const query = useInfiniteQuery({
    queryKey: ['inbound-slideout-patient-list', queryParams, analyticsFilters],
    queryFn: ({ pageParam }) =>
      retrieveAnalytics({ ...queryParams, ...((pageParam ?? {}) as Record<string, unknown>) }, analyticsFilters),
    enabled: !!analyticsFilters,
    initialPageParam: queryParams as Record<string, unknown>,
    getNextPageParam: (lastPage: Analytics) =>
      (lastPage?.links?.next?.payload as Record<string, unknown> | undefined) ?? null,
  });

  const rows = useMemo<PatientRow[]>(() => {
    const pages = query.data?.pages ?? [];
    if (!pages.length) return [];
    const headers = pages[0]?.dimensionHeaders ?? [];
    const idx = (name: string) => headers.findIndex((h) => h.name === name);
    const patientNameIdx = idx(Dimensions.PATIENT_NAME);
    const urlIdx = idx(Dimensions.REFERRAL_URL);
    const receivingFacilitiesIdx = idx(Dimensions.RECEIVING_FACILITIES);
    const referralSourceIdx = idx(Dimensions.REFERRAL_SOURCE);
    const payerSourceIdx = idx(Dimensions.PAYER_SOURCE);
    const dateIdx = idx(Dimensions.DATE);
    return pages.flatMap((page) =>
      (page?.data ?? []).map((row): PatientRow => {
        const dimValue = (i: number) => (i >= 0 ? row.dimensionValues[i]?.value : undefined);
        return {
          patientName: String(dimValue(patientNameIdx) ?? ''),
          url: String(dimValue(urlIdx) ?? ''),
          receivingFacilities: String(dimValue(receivingFacilitiesIdx) ?? ''),
          referralSource: String(dimValue(referralSourceIdx) ?? ''),
          payerSource: String(dimValue(payerSourceIdx) ?? ''),
          receivedAt: String(dimValue(dateIdx) ?? ''),
        };
      })
    );
  }, [query.data]);

  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const { customRef, isIntersecting } = useIntersectionObserver({ threshold: 0.2, root: scrollAreaRef.current });

  // Depend on the individual values rather than the whole query object, which gets a new identity
  // on nearly every render — otherwise this effect re-runs constantly and the fetch guard is only
  // load-bearing by accident.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  useEffect(() => {
    if (isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isIntersecting, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const table = useReactTable({
    columns,
    data: rows,
    state: { sorting: sort },
    onSortingChange: (updater) => {
      const next = updater instanceof Function ? updater(sort) : updater;
      setSort(next);
    },
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const showLoading = query.isLoading;
  const showEmpty = !query.isLoading && rows.length === 0;
  const showNextSpinner = query.isFetchingNextPage;

  const visibleColumns = table.getVisibleLeafColumns();
  const tracks = buildGridTemplateTracks(visibleColumns);

  return (
    <ScrollArea ref={scrollAreaRef}>
      <StyledTable
        $theme={DataTableTheme.DEFAULT}
        $freezeFirstColumn
        $gridTemplateColumns={tracks.join(' ')}
        $mobileGridTemplateColumns={
          visibleColumns.length ? buildMobileGridTemplateColumns(tracks, visibleColumns[0].getSize()) : undefined
        }>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderFooterRow key={headerGroup.id} $theme={DataTableTheme.DEFAULT}>
              {headerGroup.headers.map((header) => (
                <TableHeaderCell
                  key={header.id}
                  isSorted={!!header.column.getIsSorted()}
                  isSortable={header.column.getCanSort()}
                  onClick={header.column.getToggleSortingHandler()}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && (
                    <ArrowUpDownIcon
                      direction={header.column.getIsSorted() || 'none'}
                      height={10}
                      width={10}
                      style={{ marginLeft: 8 }}
                    />
                  )}
                </TableHeaderCell>
              ))}
            </TableHeaderFooterRow>
          ))}
        </TableHeader>
        <TableBody>
          {showLoading && (
            <TableRow>
              <LoadingCell colSpan={columns.length}>
                <CircularProgress size={24} thickness={3} />
              </LoadingCell>
            </TableRow>
          )}
          {showEmpty && (
            <TableRow>
              <EmptyCell colSpan={columns.length}>No referrals found for this period.</EmptyCell>
            </TableRow>
          )}
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
              ))}
            </TableRow>
          ))}
          <SentinelRow ref={customRef} />
          {showNextSpinner && (
            <TableRow>
              <SpinnerCell colSpan={columns.length}>
                <CircularProgress size={16} thickness={2} />
              </SpinnerCell>
            </TableRow>
          )}
        </TableBody>
      </StyledTable>
    </ScrollArea>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
`;

const ScrollArea = styled.div`
  max-height: 520px;
  overflow: auto;
  container-type: inline-size;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;

  td:first-child:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.colors.black10};
    /* Stretch the divider cell to the full row height so its border spans multi-line rows
       (e.g. a wrapped Receiving SNFs list) instead of stopping at its own content height. */
    align-self: stretch;
  }

  @media (max-width: 800px) {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-left: none;
    border-right: none;
    border-bottom: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 24px 24px 16px;
`;

const SentinelRow = styled.tr`
  grid-column: 1 / -1;
  display: block;
  height: 1px;
`;

const SpinnerCell = styled.td`
  display: flex;
  justify-content: center;
  padding: 12px;
  width: 100%;
  color: ${({ theme }) => theme.colors.blue100};
`;

const Title = styled.h3`
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  color: ${({ theme }) => theme.colors.black100};
`;

const ReferralLink = styled.a`
  color: ${({ theme }) => theme.colors.blue100};
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyCell = styled.td`
  padding: 24px;
  width: 100%;
  text-align: center;
  color: ${({ theme }) => theme.colors.black50};
`;

const LoadingCell = styled.td`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: ${({ theme }) => theme.colors.blue100};
`;
